import React from "react";
import "./termsAndConditions.scss";
import { BasicIcon, Tooltip, ToolTipPosition, Tag, HeaderDescription } from "@pickit/pickit-components";
import { useTranslation } from "react-i18next";
import "./i18n";
import { ISO8601toDDMMYYYHHMM } from "utils/date";
import { TermsAndConditionsProps } from "./types";
import { PdfUploadCard } from "component/documentManager/PdfUploadCard";
import { VersionHistoryCard } from "component/documentManager/VersionHistoryCard";

export const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  testId = "terms-and-conditions",
  currentFile,
  fileStatus,
  termsVersions,
  loading,
  fileHandler,
  goToPreviousFile,
  downloadCurrentFile,
  downloadVersion,
  viewVersion,
  onUpdate,
  onDeleteFile,
  onFileTypeError,
}): JSX.Element => {
  const { t } = useTranslation();

  const tableHeaders = [
    {
      label: t("termsAndConditions:version"),
      key: "version",
      textConfig: {
        formatter: (value: string) => `V${value}`,
        weight: 700,
      },
      col: 3,
      testId: "version",
    },
    { label: t("termsAndConditions:estado"), key: "estado", col: 4, testId: "state" },
    { label: t("termsAndConditions:uploadDate"), key: "uploadDate", col: 5, testId: "uploadDate" },
    { label: t("termsAndConditions:uploadedBy"), key: "uploadedBy.email", testId: "uploadedBy" },
    { label: t("termsAndConditions:acciones"), key: "actions", col: 3, testId: "actions" },
  ];

  const tableData =
    termsVersions && Array.isArray(termsVersions)
      ? termsVersions.map((version, index) => ({
          version: version.version || "N/A",
          estado:
            index === 0 ? (
              <Tag status="success" label={t("termsAndConditions:activa")} />
            ) : null,
          uploadDate: version.uploadedAt
            ? ISO8601toDDMMYYYHHMM(version.uploadedAt)
            : "N/A",
          uploadedBy: { email: version.uploadedBy?.email || "N/A" },
          actions: (
            <div className="actions-container">
              <Tooltip
                message={t("termsAndConditions:ver")}
                position={ToolTipPosition.top}
              >
                <div data-testId={`${testId}-view-version-${version.version}`}>
                  <BasicIcon
                    clickable={true}
                    onClick={() => viewVersion(version.url || "")}
                    name="eye"
                    size="small"
                  />
                </div>
              </Tooltip>
              <div data-testId={`${testId}-download-version-${version.version}`}>
                <Tooltip message={t("global:download")} position={ToolTipPosition.top}>
                  <BasicIcon
                    clickable={true}
                    onClick={() =>
                      downloadVersion(
                        version.id,
                        version.url || "",
                        version.fileName ||
                          `terminos-y-condiciones-v${version.version}.pdf`
                      )
                    }
                    name="download"
                    size="small"
                  />
                </Tooltip>
              </div>
            </div>
          ),
        }))
      : [];

  return (
    <div className="terms-and-conditions-page">
      <HeaderDescription
        headline={t("termsAndConditions:title")}
        overline="Configuraciones"
        testId={`${testId}-title`}
        description={t("termsAndConditions:subtitle")}
      />
      <div className="terms-content">
        <div className="terms-two-columns">
          <PdfUploadCard
            testId={testId}
            title={t("termsAndConditions:updateFile")}
            tooltipMessage={t("termsAndConditions:tooltipInfo")}
            fileLabel={t("termsAndConditions:fileLabel")}
            loadButtonText={t("termsAndConditions:loadFile")}
            updateButtonLabel={t("termsAndConditions:actualizar")}
            currentFile={currentFile}
            fileStatus={fileStatus}
            fileHandler={fileHandler}
            goToPreviousFile={goToPreviousFile}
            onDeleteFile={onDeleteFile}
            onUpdate={onUpdate}
            onFileTypeError={onFileTypeError}
          />
          <VersionHistoryCard
            testId={testId}
            title={t("termsAndConditions:versionHistory")}
            headers={tableHeaders}
            data={tableData}
            loading={loading}
            loadingText={
              t("termsAndConditions:loadingVersions") || "Cargando versiones..."
            }
            emptyText={t("termsAndConditions:noVersions")}
          />
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
